import React, { useEffect, useRef, useState } from 'react';
import Badge from '../UI/Badge';
import { calculateAgeFromDOB, capitalizeFirstLetter, convertSecondsToTime } from '../../helpers';
import Spacing from '../Spacing';
import { Headphones } from 'lucide-react';
import { Drawer } from 'vaul';

export default function CCMSinglePatientViewModal({ children, ...props }) {
  const {
    ccm_patient_data = {},
    activeModal,
    setActiveModal = () => {},
    previousScrollPosition = 0,
  } = props;
  const [isPlaying, setIsPlaying] = useState(false);
  const [countTime, setCountTime] = useState(0);
  const audioRef = useRef(null);
  const countIntervalRef = useRef(null);
  const dropdownRef = useRef(null);

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        clearInterval(countIntervalRef.current);
      } else {
        audioRef.current.play();
        countIntervalRef.current = setInterval(() => {
          setCountTime((prev) => {
            if (prev >= ccm_patient_data.recordingDuration) {
              clearInterval(countIntervalRef.current);
              return prev;
            }
            return prev + 1;
          });
        }, 1000);
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;

    // Handle when the audio ends
    const handleAudioEnd = () => {
      setIsPlaying(false);
      clearInterval(countIntervalRef.current);
    };

    if (audio) {
      audio.addEventListener("ended", handleAudioEnd);
    }

    return () => {
      if (audio) {
        audio.removeEventListener("ended", handleAudioEnd);
      }
      clearInterval(countIntervalRef.current);
    };
  }, []);

  const modalStatus = (status) => {
    setActiveModal(status);
  };

  const renderCCMTextIntoNewLines = (data) => {
    if (!data || typeof data !== 'object') return null; // Ensure data is valid and an object

    const renderSection = (title, content) => (
      <div key={title}>
        <strong>{capitalizeFirstLetter(title)}</strong>
        <p>{content}</p>
      </div>
    );

    const renderList = (title, items) => {
      if (!Array.isArray(items)) {
        return null;
      }
      return (
        <div key={title}>
          <strong>{capitalizeFirstLetter(title)}:</strong>
          <ul>
            {items.map((item, idx) => {
              if (typeof item === 'string') {
                return <li key={idx}>{item}</li>;
              }
              if (typeof item === 'object') {
                return (
                  <li key={idx}>
                    {Object.entries(item).map(([key, value]) => (
                      <div key={key}>
                        <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                      </div>
                    ))}
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
      );
    };
    

    const renderNestedObject = (title, obj) => (
      <div key={title}>
        <strong>{capitalizeFirstLetter(title)}</strong>
        <ul>
          {Object.entries(obj).map(([key, value]) => {
            if (typeof value === 'string') {
              return (
                <li key={key}>
                  <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                </li>
              );
            }
            if (Array.isArray(value)) {
              return (
                <li key={key}>
                  <strong>{key.replace(/_/g, ' ')}:</strong>
                  {renderList('', value)}
                </li>
              );
            }
            if (typeof value === 'object') {
              return (
                <li key={key}>
                  <strong>{key.replace(/_/g, ' ')}:</strong>
                  {renderNestedObject('', value)}
                </li>
              );
            }
            return null;
          })}
        </ul>
      </div>
    );

    return (
      <div>
        {data.chief_complaint && renderSection('Reason for Visit', data.chief_complaint)}

        {data.discussion_summary &&
          Object.entries(data.discussion_summary).map(([key, value]) => {
            if (typeof value === 'string') {
              return renderSection(key.replace(/_/g, ' '), value);
            }
            if (Array.isArray(value)) {
              return renderList(key.replace(/_/g, ' '), value);
            }
            if (typeof value === 'object') {
              return renderNestedObject(key.replace(/_/g, ' '), value);
            }
            return null;
          })}

        {data.assessment_and_plan &&
          Object.entries(data.assessment_and_plan).map(([key, value]) => {
            if (typeof value === 'string') {
              return renderSection(key.replace(/_/g, ' '), value);
            }
            if (Array.isArray(value)) {
              return renderList(key.replace(/_/g, ' '), value);
            }
            if (typeof value === 'object') {
              return renderNestedObject(key.replace(/_/g, ' '), value);
            }
            return null;
          })}

        {data.icd_codes && renderList('ICD Codes', data.icd_codes)}
      </div>
    );
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger style={{ height: '100%' }} onClick={() => modalStatus(!activeModal)}>
          {children}
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Patient Details</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
              <div className="docpro_modal" style={{ backgroundColor: '#F9F9FB' }}>
                {!ccm_patient_data ? (
                  <div
                    style={{
                      height: '300px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <>
                    <div className="docpro_patient_head" style={{ alignItems: 'start' }}>
                      <div className="docpro_media">
                        <img src="/images/avatar.png" alt="Avatar" />
                        <div className="docpro_media_right">
                          <h2 className="docpro_fs_24">
                            {ccm_patient_data?.lastName + ' ' + ccm_patient_data?.firstName}
                          </h2>

                          {ccm_patient_data?.riskLevel === 'High' && (
                            <Badge color="red">
                              <span>High-Risk Patient</span>
                            </Badge>
                          )}
                          {ccm_patient_data?.riskLevel === 'Moderate' && (
                            <Badge color="orange">
                              <span>Moderate-Risk Patient</span>
                            </Badge>
                          )}
                          {ccm_patient_data?.riskLevel === 'Low' && (
                            <Badge color="green">
                              <span>Low-Risk Patient</span>
                            </Badge>
                          )}
                          <p
                            className="docpro_fs_16 docpro_primary_color"
                            style={{ paddingLeft: '5px' }}
                          >
                            Gender: {ccm_patient_data?.sex}
                            <span className="docpro_white_color">--</span> Age:{" "}
                            {calculateAgeFromDOB(ccm_patient_data?.dob)}
                          </p>
                        </div>
                      </div>
                      {ccm_patient_data.recordingUrl && ccm_patient_data.recordingDuration &&
                        <div className="docpro_bordered_btns">

                          <div className="dropdown-container relative">
                            <button
                              type='button'
                              className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                              onClick={toggleAudio}
                            >
                              <Headphones width={17} height={17} />
                              <span>{isPlaying ? 'Pause Recording' : 'Play Recording'}</span>
                            </button>
                            <p style={{marginTop:"10px",fontWeight:"400"}}> Duration: {convertSecondsToTime(countTime)} /{" "}
                            {convertSecondsToTime(ccm_patient_data.recordingDuration)} </p>
                          </div>
                          <audio ref={audioRef} src={ccm_patient_data.recordingUrl} />
                        </div>
                      }
                    </div>
                    <Spacing lg="10" sm="10" />
                    <hr />
                    <Spacing lg="10" sm="10" />
                    <div className="docpro_tab items-center justify-between">
                      {ccm_patient_data.transcriptionSummaryJSON && (
                        <>
                          <div
                            className="docpro_message_item note-content"
                          >
                            <div className="docpro_message_item_out" style={{maxWidth:"100%"}}>
                              <div className="docpro_message_item_in">
                                <div className="docpro_message_item_text">
                                  {renderCCMTextIntoNewLines(ccm_patient_data.transcriptionSummaryJSON)}
                                </div>
                              </div>
                            </div>
                          </div>

                        </>
                      )}
                      <Spacing lg="20" sm="20" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
